import React, { useState, useEffect, useRef } from 'react';
import {
  MailOutlined,
  UserOutlined,
  CalendarOutlined,
  ToolOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  CheckOutlined,
  DownOutlined,
  EyeOutlined,
  SendOutlined,
  InfoCircleOutlined,
  UploadOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { Switch, Select } from 'antd';

const personalizationTokens = [
  { id: 'first_name', label: 'First Name', icon: <UserOutlined /> },
  { id: 'last_name', label: 'Last Name', icon: <UserOutlined /> },
  { id: 'service_date', label: 'Service Date', icon: <CalendarOutlined /> },
  { id: 'service_type', label: 'Service Type', icon: <ToolOutlined /> },
];

const cooldownOptions = [
  { value: '', label: 'No cooldown' },
  { value: '7days', label: '7 days' },
  { value: '14days', label: '14 days' },
  { value: '30days', label: '30 days' },
  { value: '45days', label: '45 days' },
  { value: '60days', label: '60 days' },
  { value: '90days', label: '90 days' },
  { value: '120days', label: '120 days' },
  { value: '150days', label: '150 days' },
  { value: '180days', label: '180 days' },
  { value: '1year', label: '1 year' },
  { value: '18months', label: '18 months' },
  { value: '24months', label: '24 months' },
  { value: '36months', label: '36 months' },
  { value: '48months', label: '48 months' },
];

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

  .etd-root {
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: #f5f6fa;
    min-height: 100vh;
    padding: 40px 32px 100px;
    color: #0f172a;
  }
  .etd-page-header {
    text-align: center;
    margin-bottom: 40px;
  }
  .etd-icon-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, var(--ink) 0%, #6366f1 100%);
    border-radius: 16px;
    font-size: 24px;
    color: #fff;
    margin-bottom: 16px;
    box-shadow: 0 8px 24px rgba(99,102,241,0.3);
  }
  .etd-page-title {
    font-size: 28px;
    font-weight: 800;
    color: #0f172a;
    margin: 0 0 8px;
    letter-spacing: -0.5px;
  }
  .etd-page-subtitle {
    font-size: 15px;
    color: #64748b;
    margin: 0;
  }
  .etd-gps-banner {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    background: linear-gradient(135deg, #eff6ff 0%, #eef2ff 100%);
    border: 1px solid #c7d2fe;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 32px;
    max-width: 1400px;
    margin-left: auto;
    margin-right: auto;
  }
  .etd-gps-banner-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, var(--ink), #6366f1);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
  }
  .etd-gps-banner strong {
    display: block;
    font-size: 14px;
    font-weight: 700;
    color: #1e40af;
    margin-bottom: 2px;
  }
  .etd-gps-banner span {
    font-size: 13px;
    color: #3730a3;
    line-height: 1.5;
  }
  .etd-layout {
    display: grid;
    grid-template-columns: 1fr 420px;
    gap: 28px;
    max-width: 1400px;
    margin: 0 auto 32px;
    align-items: start;
  }
  .etd-card {
    background: #fff;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04);
    overflow: hidden;
    margin-bottom: 24px;
  }
  .etd-card:last-child { margin-bottom: 0; }
  .etd-card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 20px 24px 16px;
    border-bottom: 1px solid #f1f5f9;
  }
  .etd-card-header-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
  }
  .etd-card-header-icon.blue { background: #eff6ff; color: var(--ink); }
  .etd-card-header-icon.green { background: #f0fdf4; color: #10b981; }
  .etd-card-title {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
  }
  .etd-card-body { padding: 20px 24px 24px; }
  .etd-field { margin-bottom: 18px; }
  .etd-field:last-child { margin-bottom: 0; }
  .etd-label {
    display: block;
    font-size: 12.5px;
    font-weight: 700;
    color: var(--ink-100);
    margin-bottom: 7px;
    letter-spacing: 0.01em;
    text-transform: uppercase;
  }
  .etd-input,
  .etd-textarea {
    width: 100%;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    padding: 11px 14px;
    font-size: 14px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    color: #0f172a;
    background: #fafbfc;
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
    outline: none;
    box-sizing: border-box;
  }
  .etd-input:focus,
  .etd-textarea:focus {
    border-color: #6366f1;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
  }
  .etd-input::placeholder,
  .etd-textarea::placeholder { color: #c0c9d6; }
  .etd-textarea { resize: vertical; line-height: 1.65; }
  .etd-hint {
    font-size: 11.5px;
    color: #94a3b8;
    margin-top: 5px;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .etd-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  .etd-subject-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 6px;
  }
  .etd-char-count {
    font-size: 11.5px;
    font-family: 'JetBrains Mono', monospace;
    color: #94a3b8;
    transition: color 0.2s;
  }
  .etd-char-count.warn { color: #f59e0b; }
  .etd-tokens {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }
  .etd-token-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1.5px solid #e2e8f0;
    background: #f8fafc;
    font-size: 12px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
    transition: all 0.15s;
  }
  .etd-token-btn:hover {
    border-color: #6366f1;
    background: #eef2ff;
    color: #4f46e5;
    transform: translateY(-1px);
  }
  .etd-preview-sticky {
    position: sticky;
    top: 24px;
  }
  .etd-preview-card {
    background: #fff;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 24px rgba(0,0,0,0.07);
    overflow: hidden;
  }
  .etd-preview-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 18px 20px 14px;
    border-bottom: 1px solid #f1f5f9;
  }
  .etd-preview-header h3 {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
  }
  .etd-preview-live-badge {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    color: #10b981;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 20px;
    padding: 3px 9px;
  }
  .etd-live-dot {
    width: 6px;
    height: 6px;
    background: #10b981;
    border-radius: 50%;
    animation: etdpulse 2s infinite;
  }
  @keyframes etdpulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  .etd-email-mockup {
    margin: 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }
  .etd-email-titlebar {
    background: #f1f5f9;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .etd-dot { width: 10px; height: 10px; border-radius: 50%; }
  .etd-email-from {
    background: #fff;
    padding: 10px 14px;
    border-bottom: 1px solid #f1f5f9;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .etd-avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea, #764ba2);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
  }
  .etd-email-from-name {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
  }
  .etd-email-from-time {
    font-size: 11px;
    color: #94a3b8;
    margin-left: auto;
    white-space: nowrap;
  }
  .etd-email-subject-bar {
    background: #fff;
    padding: 10px 14px;
    border-bottom: 1px solid #f1f5f9;
  }
  .etd-email-subject-label {
    font-size: 10.5px;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
    margin-bottom: 2px;
  }
  .etd-email-subject-text {
    font-size: 13.5px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
  }
  .etd-email-body-preview {
    background: #fff;
    padding: 14px;
    font-size: 13px;
    line-height: 1.7;
    white-space: pre-wrap;
    min-height: 200px;
    max-height: 300px;
    overflow-y: auto;
    color: var(--ink-100);
  }
  .etd-email-footer-bar {
    background: #f8fafc;
    border-top: 1px solid #f1f5f9;
    padding: 10px 14px;
    font-size: 11px;
    color: #94a3b8;
    text-align: center;
  }
  .etd-preview-tip {
    margin: 12px 16px 16px;
    padding: 10px 12px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 8px;
    font-size: 12px;
    color: #92400e;
    display: flex;
    align-items: flex-start;
    gap: 6px;
    line-height: 1.5;
  }
  .etd-delivery-section {
    max-width: 1400px;
    margin: 0 auto 24px;
  }
  .etd-section-label {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #94a3b8;
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .etd-section-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }
  .etd-setting-row {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 20px;
    border-bottom: 1px solid #f8fafc;
  }
  .etd-setting-row:last-child { border-bottom: none; }
  .etd-setting-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
    background: #f1f5f9;
    color: #64748b;
  }
  .etd-setting-text strong {
    display: block;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 1px;
  }
  .etd-setting-text span {
    font-size: 12.5px;
    color: #64748b;
  }
  .etd-cooldown-wrap { padding: 0 20px 20px; }
  .etd-cooldown-grid { display: flex; flex-wrap: wrap; gap: 8px; }
  .etd-cooldown-pill {
    padding: 7px 14px;
    border-radius: 20px;
    border: 1.5px solid #e2e8f0;
    background: #f8fafc;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
    transition: all 0.15s;
    font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .etd-cooldown-pill:hover {
    border-color: #6366f1;
    color: #4f46e5;
    background: #eef2ff;
  }
  .etd-cooldown-pill.active {
    border-color: #6366f1;
    background: #6366f1;
    color: #fff;
    box-shadow: 0 2px 8px rgba(99,102,241,0.3);
  }
  .etd-quiet-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 20px;
    background: #f8fafc;
    border-radius: 0 0 14px 14px;
  }
  .etd-quiet-row span {
    font-size: 13px;
    color: #64748b;
    font-weight: 600;
  }
  .etd-compliance-card {
    max-width: 1400px;
    margin: 0 auto 32px;
    background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
    border: 1px solid #a7f3d0;
    border-radius: 16px;
    padding: 20px 24px;
  }
  .etd-compliance-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 800;
    color: #065f46;
    margin-bottom: 10px;
  }
  .etd-compliance-header .anticon {
    font-size: 18px;
    color: #10b981;
  }
  .etd-compliance-p {
    font-size: 13px;
    color: #047857;
    margin: 0 0 10px;
  }
  .etd-compliance-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .etd-compliance-list li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 13px;
    color: #065f46;
    line-height: 1.4;
  }
  .etd-compliance-list .anticon {
    flex-shrink: 0;
    margin-top: 2px;
    color: #10b981;
    font-size: 12px;
  }
  .etd-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(255,255,255,0.95);
    backdrop-filter: blur(12px);
    border-top: 1px solid #e2e8f0;
    padding: 14px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: 100;
    box-shadow: 0 -4px 24px rgba(0,0,0,0.06);
  }
  .etd-footer-info {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
  }
  .etd-footer-info strong {
    color: #0f172a;
    font-weight: 700;
  }
  .etd-continue-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #FFB400;
    color: #0F1729;
    border: none;
    border-radius: 9999px;
    padding: 10px 28px;
    font-size: 14px;
    font-weight: 700;
    font-family: 'Plus Jakarta Sans', sans-serif;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 8px rgba(255, 180, 0, 0.3);
  }
  .etd-continue-btn:hover {
    background: #e6a200;
  }
  .etd-continue-btn:active { transform: translateY(0); }
`;

const EmailTemplateDelivery = ({ campaignData = {}, onUpdate = () => {}, onNext = () => {} }) => {
  const [campaignName, setCampaignName] = useState(campaignData.campaignName || '');
  const [senderName, setSenderName] = useState(campaignData.senderName || '');
  const [senderEmail, setSenderEmail] = useState(campaignData.senderEmail || '');
  const [replyToEmail, setReplyToEmail] = useState(campaignData.replyToEmail || '');
  const [subjectLine, setSubjectLine] = useState(campaignData.subjectLine || '');
  const [useCustomHtml] = useState(campaignData.useCustomHtml || false);
  const [emailBody, setEmailBody] = useState(campaignData.emailBody || '');
  const [cooldownPeriod, setCooldownPeriod] = useState(campaignData.cooldownPeriod !== undefined ? campaignData.cooldownPeriod : '');
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(campaignData.quietHoursEnabled !== false);
  const [quietHoursFrom, setQuietHoursFrom] = useState(campaignData.quietHoursFrom || '9:00 PM');
  const [quietHoursTo, setQuietHoursTo] = useState(campaignData.quietHoursTo || '8:00 AM');
  const [logoUrl, setLogoUrl] = useState(campaignData.logoUrl || '');
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);
  const previewFirstName = 'John';
  const previewLastName = 'Smith';

  const maxSubjectChars = 150;
  const subjectCharCount = subjectLine.length;

  useEffect(() => {
    onUpdate({
      campaignName, senderName, senderEmail, replyToEmail, subjectLine,
      useCustomHtml, emailBody, cooldownPeriod, quietHoursEnabled, quietHoursFrom, quietHoursTo, logoUrl,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignName, senderName, senderEmail, replyToEmail, subjectLine, useCustomHtml, emailBody, cooldownPeriod, quietHoursEnabled, quietHoursFrom, quietHoursTo, logoUrl]);

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch('/api/crm/logo-upload', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const data = await res.json();
      if (data.url) { setLogoUrl(data.url); }
      else { alert('Upload failed'); }
    } catch {
      alert('Failed to upload logo');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const insertToken = (tokenId) => {
    const token = '{{' + tokenId + '}}';
    setEmailBody(prev => prev + token);
  };

  const getEmailPreview = () => {
    let preview = emailBody;
    preview = preview.replace(/\{\{first_name\}\}/g, previewFirstName);
    preview = preview.replace(/\{\{last_name\}\}/g, previewLastName);
    preview = preview.replace(/\{\{service_date\}\}/g, 'Dec 15');
    preview = preview.replace(/\{\{service_type\}\}/g, 'HVAC Maintenance');
    return preview || 'Your email content will appear here...';
  };

  const senderInitial = (senderName || 'Y').charAt(0).toUpperCase();

  return (
    <>
      <style>{styles}</style>
      <div className="etd-root">

        {/* Page Header */}
        <div className="etd-page-header">
          <div className="etd-icon-badge"><MailOutlined /></div>
          <h1 className="etd-page-title">Email Template &amp; Delivery</h1>
          <p className="etd-page-subtitle">Compose your professional GPS-triggered email campaign</p>
        </div>

        {/* GPS Banner */}
        <div className="etd-gps-banner">
          <div className="etd-gps-banner-icon"><ClockCircleOutlined /></div>
          <div>
            <strong>GPS-Triggered Email Campaign</strong>
            <span>Emails are sent automatically when your vehicles enter the notification range. Configure your message below and it will be sent to your selected audience.</span>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="etd-layout">

          {/* LEFT COLUMN */}
          <div>

            {/* Campaign Info Card */}
            <div className="etd-card">
              <div className="etd-card-header">
                <div className="etd-card-header-icon blue"><MailOutlined /></div>
                <h3 className="etd-card-title">Campaign Information</h3>
              </div>
              <div className="etd-card-body">

                <div className="etd-field">
                  <label className="etd-label">Campaign Name <span style={{ color: '#6366f1' }}>*</span></label>
                  <input
                    type="text"
                    className="etd-input"
                    placeholder="e.g., Spring HVAC Maintenance Campaign"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                  />
                  <div className="etd-hint"><InfoCircleOutlined /> Internal reference name for this campaign</div>
                </div>

                <div className="etd-grid-2">
                  <div className="etd-field">
                    <label className="etd-label">Sender Name <span style={{ color: '#6366f1' }}>*</span></label>
                    <input
                      type="text"
                      className="etd-input"
                      placeholder="e.g., ABC Services"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                    />
                  </div>
                  <div className="etd-field">
                    <label className="etd-label">Sender Email <span style={{ color: '#94a3b8', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(Optional)</span></label>
                    <input
                      type="email"
                      className="etd-input"
                      placeholder="Leave empty to use account email"
                      value={senderEmail}
                      onChange={(e) => setSenderEmail(e.target.value)}
                    />
                    <div className="etd-hint">Defaults to your login email</div>
                  </div>
                </div>

                <div className="etd-field">
                  <label className="etd-label">Reply-To Email <span style={{ color: '#94a3b8', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(Optional)</span></label>
                  <input
                    type="email"
                    className="etd-input"
                    placeholder="e.g., support@company.com"
                    value={replyToEmail}
                    onChange={(e) => setReplyToEmail(e.target.value)}
                  />
                  <div className="etd-hint">Where replies will be directed</div>
                </div>

              </div>
            </div>

            {/* Email Content Card */}
            <div className="etd-card">
              <div className="etd-card-header">
                <div className="etd-card-header-icon green"><SendOutlined /></div>
                <h3 className="etd-card-title">Email Content</h3>
              </div>
              <div className="etd-card-body">

                <div className="etd-field">
                  <label className="etd-label"><PictureOutlined style={{ marginRight: 6 }} />Company Logo <span style={{ color: '#94a3b8', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(Optional)</span></label>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      className="etd-input"
                      placeholder="https://yoursite.com/logo.png"
                      value={logoUrl}
                      onChange={(e) => setLogoUrl(e.target.value)}
                      style={{ flex: 1, minWidth: 200 }}
                    />
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={logoUploading}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: logoUploading ? '#e2e8f0' : '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, cursor: logoUploading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}
                    >
                      <UploadOutlined />{logoUploading ? 'Uploading...' : 'Upload Logo'}
                    </button>
                    <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoFileChange} />
                  </div>
                  {logoUrl && (
                    <div style={{ marginTop: 10, padding: '8px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <img src={logoUrl} alt="Logo" style={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                      <button type="button" onClick={() => setLogoUrl('')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
                    </div>
                  )}
                  <div className="etd-hint"><InfoCircleOutlined /> Logo appears at the top of every email sent</div>
                </div>

                <div className="etd-field">
                  <label className="etd-label">Subject Line <span style={{ color: '#6366f1' }}>*</span></label>
                  <input
                    type="text"
                    className="etd-input"
                    placeholder="e.g., We're in your area today!"
                    value={subjectLine}
                    onChange={(e) => setSubjectLine(e.target.value.slice(0, maxSubjectChars))}
                    style={{ fontWeight: 600 }}
                  />
                  <div className="etd-subject-footer">
                    <div className="etd-hint">Use first_name, last_name tokens for personalization</div>
                    <div className={'etd-char-count' + (subjectCharCount > 100 ? ' warn' : '')}>{subjectCharCount} / {maxSubjectChars}</div>
                  </div>
                </div>

                <div className="etd-field">
                  <label className="etd-label">Email Message <span style={{ color: '#6366f1' }}>*</span></label>
                  <div className="etd-hint" style={{ marginBottom: '10px' }}>Insert personalization tokens:</div>
                  <div className="etd-tokens">
                    {personalizationTokens.map(token => (
                      <button key={token.id} className="etd-token-btn" onClick={() => insertToken(token.id)}>
                        {token.icon} {token.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    className="etd-textarea etd-input"
                    placeholder={'Hi {{first_name}},\n\nWe\'re servicing homes in your neighborhood today and wanted to reach out about our special offer.\n\n[Your message here]\n\nBest regards,\n' + (senderName || 'Your Company')}
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    rows={11}
                  />
                  <div className="etd-hint"><InfoCircleOutlined /> Unsubscribe link will be added automatically to every email.</div>
                </div>

              </div>
            </div>

          </div>

          {/* RIGHT COLUMN - Sticky Preview */}
          <div className="etd-preview-sticky">
            <div className="etd-preview-card">
              <div className="etd-preview-header">
                <div className="etd-card-header-icon blue" style={{ width: 32, height: 32, fontSize: 14 }}><EyeOutlined /></div>
                <h3>Live Preview</h3>
                <div className="etd-preview-live-badge">
                  <div className="etd-live-dot" />
                  Live
                </div>
              </div>

              {/* Inbox mockup header */}
              <div className="etd-email-mockup">
                <div className="etd-email-titlebar">
                  <div className="etd-dot" style={{ background: '#fc5c65' }} />
                  <div className="etd-dot" style={{ background: '#fed330' }} />
                  <div className="etd-dot" style={{ background: '#26de81' }} />
                  <div style={{ flex: 1, background: '#e2e8f0', borderRadius: 4, height: 14, marginLeft: 10 }} />
                </div>

                <div className="etd-email-from">
                  <div className="etd-avatar">{senderInitial}</div>
                  <div>
                    <div className="etd-email-from-name">{senderName || 'Your Company Name'}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>to: john.smith@email.com</div>
                  </div>
                  <div className="etd-email-from-time">Just now</div>
                </div>

                <div className="etd-email-subject-bar">
                  <div className="etd-email-subject-label">Subject</div>
                  <div className="etd-email-subject-text">
                    {subjectLine || <span style={{ color: '#c0c9d6' }}>Your subject line will appear here...</span>}
                  </div>
                </div>

                {/* Actual email body — matches backend HTML template exactly */}
                <div style={{ background: '#f4f5f7', padding: '24px 12px' }}>
                  <div style={{
                    maxWidth: 600,
                    margin: '0 auto',
                    background: '#fff',
                    borderRadius: 12,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  }}>
                    {logoUrl && (
                      <div style={{ textAlign: 'center', padding: '28px 24px 8px' }}>
                        <img src={logoUrl} alt="Logo" style={{ maxHeight: 52, maxWidth: 180, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                    )}
                    <div style={{
                      padding: '28px 36px 32px',
                      fontSize: 15,
                      lineHeight: 1.75,
                      color: '#333333',
                      fontFamily: "Arial, 'Helvetica Neue', Helvetica, sans-serif",
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      minHeight: 120,
                    }}>
                      {getEmailPreview() || <span style={{ color: '#c0c9d6' }}>Your email content will appear here...</span>}
                    </div>
                    <div style={{ padding: '0 36px' }}>
                      <hr style={{ border: 'none', borderTop: '1px solid #e8e8e8', margin: 0 }} />
                    </div>
                    <div style={{ padding: '20px 36px 28px', textAlign: 'center' }}>
                      <p style={{ fontSize: 13, color: '#888888', margin: '0 0 4px' }}>{senderName || 'Our Team'}</p>
                      <p style={{ fontSize: 11, color: '#aaaaaa', margin: 0 }}>
                        You received this email because you opted in to hear from us.<br/>
                        <button 
                          type="button" 
                          onClick={e => e.preventDefault()} 
                          style={{ 
                            background: 'none', 
                            border: 'none', 
                            color: 'var(--ink-200)', 
                            textDecoration: 'underline', 
                            padding: 0, 
                            cursor: 'pointer',
                            font: 'inherit'
                          }}
                        >
                          Unsubscribe
                        </button> · Reply STOP to opt out
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="etd-preview-tip">
                <InfoCircleOutlined style={{ flexShrink: 0, marginTop: 1 }} /> This is an exact preview of what the recipient will see in their inbox.
              </div>
            </div>
          </div>
        </div>

        <div className="etd-delivery-section">
          <div className="etd-section-label">Delivery Settings</div>

          <div className="etd-card">
            <div className="etd-setting-row">
              <div className="etd-setting-icon"><ClockCircleOutlined /></div>
              <div className="etd-setting-text">
                <strong>Cooldown Period</strong>
                <span>Minimum time between emails to the same contact</span>
              </div>
            </div>
            <div className="etd-cooldown-wrap">
              <div className="etd-cooldown-grid">
                {cooldownOptions.map(opt => (
                  <button
                    key={opt.value}
                    className={'etd-cooldown-pill' + (cooldownPeriod === opt.value ? ' active' : '')}
                    onClick={() => setCooldownPeriod(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="etd-setting-row" style={{ borderTop: '1px solid #f1f5f9' }}>
              <div className="etd-setting-icon"><ClockCircleOutlined /></div>
              <div className="etd-setting-text">
                <strong>Quiet Hours</strong>
                <span>Do not send emails during these hours (recipient local time)</span>
              </div>
              <Switch
                checked={quietHoursEnabled}
                onChange={setQuietHoursEnabled}
                style={{ marginLeft: 'auto', flexShrink: 0 }}
              />
            </div>
            {quietHoursEnabled && (
              <div className="etd-quiet-row">
                <span>From</span>
                <Select
                  value={quietHoursFrom}
                  onChange={setQuietHoursFrom}
                  style={{ width: 130 }}
                  options={[
                    { value: '8:00 PM', label: '8:00 PM' },
                    { value: '9:00 PM', label: '9:00 PM' },
                    { value: '10:00 PM', label: '10:00 PM' },
                  ]}
                />
                <span>To</span>
                <Select
                  value={quietHoursTo}
                  onChange={setQuietHoursTo}
                  style={{ width: 130 }}
                  options={[
                    { value: '7:00 AM', label: '7:00 AM' },
                    { value: '8:00 AM', label: '8:00 AM' },
                    { value: '9:00 AM', label: '9:00 AM' },
                  ]}
                />
              </div>
            )}
          </div>
        </div>

        {/* CAN-SPAM Compliance */}
        <div className="etd-compliance-card">
          <div className="etd-compliance-header">
            <SafetyOutlined /> CAN-SPAM Compliance
          </div>
          <p className="etd-compliance-p">By launching this campaign, you confirm that:</p>
          <ul className="etd-compliance-list">
            <li><CheckOutlined /> All contacts have provided express written consent to receive email messages</li>
            <li><CheckOutlined /> You will honor all opt-out requests within 24 hours</li>
            <li><CheckOutlined /> Messages will include your business name and opt-out instructions</li>
            <li><CheckOutlined /> You maintain records of consent for each contact</li>
          </ul>
        </div>

        {/* Sticky Footer */}
        <div className="etd-footer">
          <div className="etd-footer-info">
            {campaignName ? <span>Campaign: <strong>{campaignName}</strong></span> : 'Create your email campaign'}
          </div>
          <button className="etd-continue-btn" onClick={onNext}>
            Continue <DownOutlined />
          </button>
        </div>

      </div>
    </>
  );
};

export default EmailTemplateDelivery;